/**
 * Copy for /use-cases/private-meeting-transcription.
 *
 * Compliance gate (mirrors MINA-49 / MINA-53 policy): this page intentionally avoids
 * naming specific compliance frameworks (HIPAA, GDPR, SOC2) and avoids
 * legal-grade claims about evidentiary admissibility or certified
 * compliance. The factual posture is limited to what the engineering
 * team can verify today: audio is captured locally on macOS/Windows,
 * processed via reviewed cloud AI paths with zero data retention, the transcript
 * is stored in the desktop app's local SQLite, and no third-party meeting bot joins the call.
 *
 * ME-144: renamed from the legacy private-transcription slug.
 * Reframed wedge — "privacy-first cloud chain + no bot + transcripts stay on your machine".
 * Prior factual inaccuracies (local-only, no cloud) have been corrected.
 * See /trust for the full provider data-flow.
 */
import type { Lang } from "../index";

export interface PrivateMeetingTranscriptionCopy {
  seo: { title: string; description: string };
  eyebrow: string;
  h1: string;
  lede: string;
  problem: { heading: string; body1: string; body2: string };
  scenarios: {
    heading: string;
    items: { tag: string; title: string; body: string }[];
  };
  how: {
    heading: string;
    body: string;
    bullets: string[];
  };
  proof: { heading: string; bullets: string[] };
  cta: { heading: string; body: string; primary: string; secondary: string };
}

const en: PrivateMeetingTranscriptionCopy = {
  seo: {
    title: "Private meeting transcription — no bot, no extension, narrow cloud chain",
    description:
      "Audio captured locally, transcribed through a reviewed cloud chain (Deepgram, OpenAI, Groq fallback), transcripts and notes stored in your desktop app. No bot in the call. No meeting hijacked. See where every payload goes in our Trust page.",
  },
  eyebrow: "Use case · Privacy-first",
  h1: "For calls the cloud shouldn't keep.",
  lede:
    "Some calls can't go through a permanent-storage transcription service. Minuta captures audio locally on your computer and processes it via a reviewed, narrow cloud chain with a strict zero-retention policy — no bot in the room, no permanent storage of your conversations, transcripts saved in the desktop app.",
  problem: {
    heading: "When the audio is the asset",
    body1:
      "Your call has someone who would notice if it were recorded by a third-party processor. The lawyer's client. The patient. The CEO on the other side of the M&A. Or your own security team that flagged cloud uploads. You currently don't transcribe these calls — you take notes by hand, you forget half of them, and the follow-ups are weaker than they should be.",
    body2:
      "Existing options force a choice. SaaS tools stream and store audio on their servers. Minuta captures audio locally and routes it through a short, reviewed cloud chain — Deepgram for speech-to-text with OpenAI or Groq as fallback — under strict zero-retention terms. Audio is processed transiently and not retained beyond the API call. The transcript file stays in your desktop app's local SQLite database.",
  },
  scenarios: {
    heading: "Where it earns its keep",
    items: [
      {
        tag: "Legal / privileged",
        title: "Client-privileged audio — narrow cloud path, zero retention",
        body: "You're prepping a deposition or running a privileged client call. Minuta captures system audio on your computer, routes it through a reviewed transcription provider under zero-retention terms, and stores the transcript in the desktop app. No third-party bot joins. No long-term cloud storage. See the full provider list on our Trust page.",
      },
      {
        tag: "Healthcare / regulated",
        title: "Patient discussion — controlled payloads, local transcript",
        body: "Care-coordination call where the audio is sensitive. Minuta keeps the final transcript in your local desktop app, using a short reviewed cloud chain with no data retention. No third-party bot in the path, no long-term storage hop. Audio payloads are narrow — Minuta sends audio, gets text back, and the provider discards the payload.",
      },
      {
        tag: "M&A / exec",
        title: "The acqui-hire conversation that can't leak",
        body: "Pre-LOI conversation with the founder you're acquiring. Minuta is invisible to call participants, captures system audio locally, routes a narrow audio payload to a reviewed transcription provider, and stores the result in your desktop app. No bot, no permanent cloud logs, no audit trail outside your machine.",
      },
    ],
  },
  how: {
    heading: "How it plugs in",
    body:
      "Open Minuta a minute before the call. Run the call as you always would — Zoom, Teams, Meet, FaceTime. Minuta captures system audio on the computer and routes it through a reviewed, zero-retention cloud chain.",
    bullets: [
      "Local capture, transient processing. System audio is captured on your computer, sent to Deepgram for transcription (OpenAI or Groq as fallback) under zero-retention terms, then discarded by the provider. Only the transcript text comes back.",
      "No bot in the call — Zoom, Teams, Meet, FaceTime, native phone bridge, it doesn't matter. The other party doesn't see a third-party participant.",
      "Transcript stored locally. The transcript file lives in the desktop app's local SQLite database on your machine. Encrypt your disk, back up to your own NAS, delete after your retention period.",
      "Pro features (copilot answer drafts, summarization) also use reviewed, zero-retention cloud APIs. Providers do not train on your meeting content.",
    ],
  },
  proof: {
    heading: "Why a reviewed cloud chain is a different category",
    bullets: [
      "Traditional services stream and store audio on their cloud permanently. Minuta uses a short, reviewed cloud chain — Deepgram, OpenAI, Groq — where each provider operates under zero-retention terms for your payloads.",
      "No bot in the call is a hard requirement, not a feature. The presence of a third-party recorder in a privileged conversation is a legal liability.",
      "Narrow payloads. Minuta sends audio, gets transcript text back. It does not send full meeting recordings to a cloud storage bucket.",
      "See every provider and what they see in our Trust page — the full written data-flow, not a marketing summary.",
    ],
  },
  cta: {
    heading: "Try it on your next sensitive call",
    body: "7 days with everything unlocked. No credit card. €9/mo after.",
    primary: "Start free trial",
    secondary: "Download for macOS",
  },
};

const pt: PrivateMeetingTranscriptionCopy = {
  seo: {
    title: "Transcrição de reunião privada — sem bot, sem extensão, cadeia cloud revisada",
    description:
      "Áudio capturado localmente, transcrito por uma cadeia cloud revisada (Deepgram, OpenAI, fallback Groq), transcrições e notas salvas no app desktop. Sem bot na call. Veja onde cada payload vai na nossa página de Trust.",
  },
  eyebrow: "Caso de uso · Privacidade primeiro",
  h1: "Para chamadas que a nuvem não deve guardar.",
  lede:
    "Algumas chamadas não podem passar por serviços de transcrição com armazenamento permanente. O Minuta captura o áudio localmente no seu computador e o processa por uma cadeia cloud revisada e restrita, com política rigorosa de retenção zero — sem robôs na chamada, sem armazenamento permanente das suas conversas, transcrições salvas no app desktop.",
  problem: {
    heading: "Quando o áudio é o ativo",
    body1:
      "Sua chamada tem alguém que perceberia se ela fosse gravada por um robô externo. O cliente do advogado. O paciente. O CEO do outro lado do M&A. Ou sua própria equipe de segurança que barrou uploads de nuvem. Hoje você simplesmente não transcreve essas chamadas — anota à mão, esquece metade e o acompanhamento sai mais fraco.",
    body2:
      "As opções existentes forçam uma escolha. Ferramentas SaaS transmitem e armazenam o áudio em seus servidores. O Minuta captura localmente e roteia por uma cadeia cloud curta e revisada — Deepgram para transcrição com OpenAI ou Groq como fallback — sob termos estritos de retenção zero. O áudio é processado de forma transitória e não é retido além da chamada de API. O arquivo de transcrição fica no banco de dados SQLite local do app desktop.",
  },
  scenarios: {
    heading: "Onde se justifica o investimento",
    items: [
      {
        tag: "Jurídico / privilegiado",
        title: "Áudio privilegiado — cadeia cloud restrita, retenção zero",
        body: "Você está preparando um depoimento ou em uma ligação privilegiada com o cliente. O Minuta captura o áudio do sistema no seu computador, roteia por um provedor de transcrição revisado sob termos de retenção zero e armazena a transcrição no app desktop. Sem bot terceiro. Sem armazenamento permanente em nuvem. Veja a lista completa de provedores na nossa página de Trust.",
      },
      {
        tag: "Saúde / regulado",
        title: "Discussão de paciente — payloads controlados, transcrição local",
        body: "Chamada de coordenação de cuidados. O Minuta mantém a transcrição final no seu app desktop local, usando uma cadeia cloud curta e revisada sem retenção de dados. Sem robô na chamada, sem etapa de armazenamento de longo prazo. Os payloads de áudio são restritos — o Minuta envia o áudio, recebe o texto e o provedor descarta o payload.",
      },
      {
        tag: "M&A / executivo",
        title: "A conversa de acqui-hire que não pode vazar",
        body: "Conversa pré-LOI com o fundador que você está adquirindo. O Minuta é invisível para os participantes da call, captura o áudio do sistema localmente, roteia um payload de áudio restrito para um provedor de transcrição revisado e armazena o resultado no seu app desktop. Sem robô, sem logs permanentes em nuvem, sem rastro fora da sua máquina.",
      },
    ],
  },
  how: {
    heading: "Como entra no fluxo",
    body:
      "Abra o Minuta um minuto antes da chamada. Roda a chamada como sempre fez — Zoom, Teams, Meet ou FaceTime. O Minuta captura o áudio do sistema e roteia por uma cadeia cloud revisada com retenção zero.",
    bullets: [
      "Captura local, processamento transitório. O áudio do sistema é capturado no computador, enviado ao Deepgram para transcrição (OpenAI ou Groq como fallback) sob termos de retenção zero, depois descartado pelo provedor. Só o texto da transcrição volta.",
      "Sem robô na chamada — Zoom, Teams, Meet, tanto faz. O outro lado não vê um participante estranho.",
      "Transcrição salva localmente. O arquivo de transcrição fica no banco de dados SQLite local do app desktop na sua máquina. Criptografe o disco, faça backup em seu NAS ou exclua após o período de retenção.",
      "Recursos Pro (sugestões do copiloto, resumos) também usam APIs cloud revisadas com retenção zero. Os provedores não treinam com o conteúdo das suas reuniões.",
    ],
  },
  proof: {
    heading: "Por que uma cadeia cloud revisada é uma categoria diferente",
    bullets: [
      "Serviços tradicionais transmitem e armazenam o áudio permanentemente em suas nuvens. O Minuta usa uma cadeia cloud curta e revisada — Deepgram, OpenAI, Groq — em que cada provedor opera sob termos de retenção zero para seus payloads.",
      "Sem bot na chamada é requisito essencial, não um recurso. A presença de um participante robô em conversas privilegiadas gera passivos legais.",
      "Payloads restritos. O Minuta envia áudio e recebe de volta o texto da transcrição. Ele não envia gravações completas de reuniões para um bucket de armazenamento em nuvem.",
      "Veja cada provedor e o que eles recebem na nossa página de Trust — o fluxo de dados completo e escrito, não um resumo de marketing.",
    ],
  },
  cta: {
    heading: "Teste na sua próxima chamada sensível",
    body: "7 dias com tudo liberado. Sem cartão. €9/mês depois.",
    primary: "Começar trial grátis",
    secondary: "Baixar para macOS",
  },
};

const es: PrivateMeetingTranscriptionCopy = {
  seo: {
    title: "Transcripción de reunión privada — sin bot, sin extensión, cadena cloud revisada",
    description:
      "Audio capturado localmente, transcrito por una cadena cloud revisada (Deepgram, OpenAI, fallback Groq), transcripciones y notas guardadas en la app desktop. Sin bot en la call. Ve adónde va cada payload en nuestra página de Trust.",
  },
  eyebrow: "Caso de uso · Privacidad primero",
  h1: "Para llamadas que la nube no debe conservar.",
  lede:
    "Algunas llamadas no pueden pasar por servicios de transcripción con almacenamiento permanente. Minuta captura el audio localmente en tu computadora y lo procesa por una cadena cloud revisada y restringida, con política estricta de retención cero — sin robots en la llamada, sin almacenamiento permanente de tus conversaciones, transcripciones guardadas en la app desktop.",
  problem: {
    heading: "Cuando el audio es el activo",
    body1:
      "Tu llamada tiene a alguien que notaría si la conversación fuera grabada por un robot externo. El cliente del abogado. El paciente. El CEO del otro lado del M&A. O tu propio equipo de seguridad que bloqueó las subidas a la nube. Hoy simplemente no transcribes estas llamadas — tomas notas a mano, olvidas la mitad y el seguimiento sale más débil.",
    body2:
      "Las opciones existentes fuerzan una elección. Las herramientas SaaS transmiten y almacenan el audio en sus servidores. Minuta captura localmente y lo enruta por una cadena cloud corta y revisada — Deepgram para transcripción con OpenAI o Groq como fallback — bajo términos estrictos de retención cero. El audio se procesa de forma transitoria y no se retiene más allá de la llamada API. El archivo de transcripción queda en la base de datos SQLite local de la app desktop.",
  },
  scenarios: {
    heading: "Dónde se justifica la inversión",
    items: [
      {
        tag: "Legal / privilegiado",
        title: "Audio privilegiado — cadena cloud restringida, retención cero",
        body: "Estás preparando una deposición o en una llamada privilegiada con el cliente. Minuta captura el audio del sistema en tu computadora, lo enruta por un proveedor de transcripción revisado bajo términos de retención cero y almacena la transcripción en la app desktop. Sin bot de terceros. Sin almacenamiento permanente en la nube. Ve la lista completa de proveedores en nuestra página de Trust.",
      },
      {
        tag: "Salud / regulado",
        title: "Discusión de paciente — payloads controlados, transcripción local",
        body: "Llamada de coordinación de cuidados. Minuta mantiene la transcripción final en tu app desktop local, usando una cadena cloud corta y revisada sin retención de datos. Sin robot en la llamada, sin paso de almacenamiento a largo plazo. Los payloads de audio son restringidos — Minuta envía el audio, recibe el texto y el proveedor descarta el payload.",
      },
      {
        tag: "M&A / ejecutivo",
        title: "La conversación de acqui-hire que no puede filtrarse",
        body: "Conversación pre-LOI con el fundador que estás adquiriendo. Minuta es invisible para los participantes de la call, captura el audio del sistema localmente, enruta un payload de audio restringido a un proveedor de transcripción revisado y almacena el resultado en tu app desktop. Sin robot, sin registros permanentes en la nube, sin rastro fuera de tu máquina.",
      },
    ],
  },
  how: {
    heading: "Cómo entra en el flujo",
    body:
      "Abre Minuta un minuto antes de la llamada. Corre la llamada como siempre — Zoom, Teams, Meet o FaceTime. Minuta captura el audio del sistema y lo enruta por una cadena cloud revisada con retención cero.",
    bullets: [
      "Captura local, procesamiento transitorio. El audio del sistema se captura en la computadora, se envía a Deepgram para transcripción (OpenAI o Groq como fallback) bajo términos de retención cero, luego el proveedor lo descarta. Solo el texto de la transcripción vuelve.",
      "Sin robot en la llamada — Zoom, Teams, Meet, da igual. El otro lado no ve un participante extraño.",
      "Transcripción guardada localmente. El archivo de transcripción vive en la base de datos SQLite local de la app desktop en tu máquina. Cifra el disco, haz backup en tu NAS o bórrala tras el período de retención.",
      "Las funciones Pro (sugerencias del copiloto, resúmenes) también usan APIs cloud revisadas con retención cero. Los proveedores no entrenan con el contenido de tus reuniones.",
    ],
  },
  proof: {
    heading: "Por qué una cadena cloud revisada es una categoría diferente",
    bullets: [
      "Los servicios tradicionales transmiten y guardan audio permanentemente en sus nubes. Minuta usa una cadena cloud corta y revisada — Deepgram, OpenAI, Groq — donde cada proveedor opera bajo términos de retención cero para tus payloads.",
      "Sin bot en la llamada es requisito esencial, no una función. La presencia de un participante robot en conversaciones privilegiadas genera pasivos legales.",
      "Payloads restringidos. Minuta envía audio y recibe de vuelta el texto de la transcripción. No envía grabaciones completas de reuniones a un bucket de almacenamiento en la nube.",
      "Ve cada proveedor y lo que reciben en nuestra página de Trust — el flujo de datos completo y escrito, no un resumen de marketing.",
    ],
  },
  cta: {
    heading: "Pruébalo en tu próxima llamada sensible",
    body: "7 días con todo desbloqueado. Sin tarjeta. €9/mes después.",
    primary: "Comenzar prueba gratis",
    secondary: "Descargar para macOS",
  },
};

export const privateMeetingTranscriptionCopy: Record<Lang, PrivateMeetingTranscriptionCopy> = { en, pt, es };
